import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { getCountryCallingCode } from "react-phone-number-input";

const mapOptions = (options) =>
  options
    .filter((option) => !option.divider)
    .map((option) => ({
      code: option.value || undefined,
      label: option.label,
    }));

const PhoneCountryAutocomplete = forwardRef(function PhoneCountryAutocomplete(
  props,
  ref
) {
  const {
    options,
    value,
    onChange,
    disabled,
    readOnly,
    onFocus,
    onBlur,
    iconComponent: IconComponent,
    className,
    "aria-label": ariaLabel,
    textFieldProps,
    ...autocompleteProps
  } = props;

  const normalizedOptions = useMemo(() => mapOptions(options), [options]);

  const selectedOption = useMemo(() => {
    const targetCode = value || undefined;
    return (
      normalizedOptions.find((option) => option.code === targetCode) ||
      null
    );
  }, [normalizedOptions, value]);

  const handleChange = (event, newValue) => {
    onChange(newValue ? newValue.code : undefined);
  };

  const getOptionLabel = (option) => option?.label || option?.code || "";

  return (
    <Autocomplete
      disabled={disabled || readOnly}
      options={normalizedOptions}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
  isOptionEqualToValue={(option, val) => (val ? option.code === val.code : false)}
      renderOption={(renderProps, option) => (
        <Box
          component="li"
          {...renderProps}
          sx={{ gap: 1.5, alignItems: "center", display: "flex" }}
        >
          {IconComponent ? (
            <IconComponent
              country={option.code}
              label={option.label || option.code || "Unknown"}
              title={option.label || option.code || "Unknown"}
            />
          ) : null}
          <Typography variant="body2" component="span">
            {option.label}
          </Typography>
        </Box>
      )}
      renderInput={(params) => {
        const fallbackLabel =
          selectedOption?.label ||
          selectedOption?.code ||
          textFieldProps?.label ||
          ariaLabel ||
          "Country";

        return (
          <TextField
            {...params}
            {...textFieldProps}
            inputRef={ref}
            label={textFieldProps?.label || ariaLabel || "Country"}
            placeholder={textFieldProps?.placeholder || "Search country"}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  {IconComponent && selectedOption ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pt: "2px",
                        pl: 0.5,
                      }}
                    >
                      <IconComponent
                        country={selectedOption.code}
                        label={selectedOption.label || selectedOption.code || "Unknown"}
                        title={selectedOption.label || selectedOption.code || "Unknown"}
                      />
                    </Box>
                  ) : null}
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              value: selectedOption && selectedOption.code
                ? `+${getCountryCallingCode(selectedOption.code)}`
                : params.inputProps.value,
              title: fallbackLabel,
            }}
            onFocus={(event) => {
              textFieldProps?.onFocus?.(event);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              textFieldProps?.onBlur?.(event);
              onBlur?.(event);
            }}
          />
        );
      }}
  className={className ? `phone-country-autocomplete ${className}` : "phone-country-autocomplete"}
      {...autocompleteProps}
    />
  );
});

PhoneCountryAutocomplete.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      divider: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  iconComponent: PropTypes.elementType,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  textFieldProps: PropTypes.object,
};

PhoneCountryAutocomplete.defaultProps = {
  value: undefined,
  disabled: false,
  readOnly: false,
  onFocus: undefined,
  onBlur: undefined,
  iconComponent: undefined,
  className: undefined,
  "aria-label": undefined,
  textFieldProps: undefined,
};

export default PhoneCountryAutocomplete;
